/**
 * Step 1: Choose Conversion Type
 *
 * User selects what type of conversion they want to track.
 */
import React from 'react';
import { cn } from '@/utils/cn';
import { ConversionCategory, CONVERSION_CATEGORIES } from '../types';

interface ChooseConversionTypeStepProps {
  category: ConversionCategory;
  onChangeCategory: (category: ConversionCategory) => void;
}

export const ChooseConversionTypeStep: React.FC<ChooseConversionTypeStepProps> = ({
  category,
  onChangeCategory,
}) => {
  return (
    <div className="space-y-6">
      {/* Step introduction */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          What do you want to track?
        </h3>
        <p className="text-sm text-gray-600">
          Choose the type of action you want to measure when people interact with your ads.
        </p>
      </div>

      {/* Conversion type options */}
      <div className="space-y-3">
        {CONVERSION_CATEGORIES.map((option) => {
          const isSelected = category === option.value;

          return (
            <label
              key={option.value}
              className={cn(
                'flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all',
                'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500',
                {
                  'border-blue-600 bg-blue-50': isSelected,
                  'border-gray-200': !isSelected,
                }
              )}
            >
              <input
                type="radio"
                name="conversionCategory"
                value={option.value}
                checked={isSelected}
                onChange={() => onChangeCategory(option.value)}
                className="sr-only"
              />
              <span className="text-2xl mr-4" role="img" aria-hidden="true">
                {option.icon}
              </span>
              <div className="flex-1">
                <p
                  className={cn('font-medium', {
                    'text-blue-900': isSelected,
                    'text-gray-900': !isSelected,
                  })}
                >
                  {option.label}
                </p>
                <p
                  className={cn('text-sm', {
                    'text-blue-700': isSelected,
                    'text-gray-500': !isSelected,
                  })}
                >
                  {option.description}
                </p>
              </div>
              {isSelected && (
                <svg
                  className="w-6 h-6 text-blue-600 ml-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </label>
          );
        })}
      </div>

      {/* Helpful tip */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex">
          <svg
            className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium">Not sure which to choose?</p>
            <p className="mt-1">
              <strong>Purchase</strong> is best for e-commerce.{' '}
              <strong>Lead</strong> is best for service businesses.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChooseConversionTypeStep;
